import React from 'react';
import getStudies from './studiesList';
import { DicomMetadataStore, log, utils, Enums } from '@ohif/core';
import isSeriesFilterUsed from '../../utils/isSeriesFilterUsed';
import authService from '../../custom/auth/authService';
import { clearPatientMode } from '../../custom/utils/patientMode';
import { isImportedStudyUID } from '@ohif/extension-default';

const { getSplitParam } = utils;

/**
 * Recreates display sets from instances already in DicomMetadataStore.
 * Needed when reopening a study: mode exit clears display sets but metadata
 * (and the study metadata promise cache) may still hold instances, so
 * addInstances dedupes and INSTANCES_ADDED never fires.
 */
function bootstrapDisplaySetsForStudies(displaySetService, studyInstanceUIDs: string[]) {
  for (const StudyInstanceUID of studyInstanceUIDs) {
    const study = DicomMetadataStore.getStudy(StudyInstanceUID);
    if (!study?.series?.length) {
      continue;
    }

    for (const series of study.series) {
      const instances = series.instances;
      if (!instances?.length) {
        continue;
      }

      const existingDisplaySets =
        displaySetService.getDisplaySetsForSeries(series.SeriesInstanceUID) || [];

      if (existingDisplaySets.length > 0) {
        continue;
      }

      displaySetService.makeDisplaySets(instances, { madeInClient: false });
    }
  }
}

/**
 * Initialize the route.
 *
 * @param props.servicesManager to read services from
 * @param props.studyInstanceUIDs for a list of studies to read
 * @param props.dataSource to read the data from
 * @param props.filters filters from query params to read the data from
 * @returns array of subscriptions to cancel
 */
export async function defaultRouteInit(
  { servicesManager, studyInstanceUIDs, dataSource, filters, appConfig }: withAppTypes,
  hangingProtocolId,
  stageIndex
) {
  const { displaySetService, hangingProtocolService, uiNotificationService, customizationService } =
    servicesManager.services;
  /**
   * Function to apply the hanging protocol when the minimum number of display sets were
   * received or all display sets retrieval were completed
   * @returns
   */
  function applyHangingProtocol() {
    const displaySets = displaySetService.getActiveDisplaySets();

    if (!displaySets || !displaySets.length) {
      return;
    }

    // Gets the studies list to use
    const studies = getStudies(studyInstanceUIDs, displaySets);

    // study being displayed, and is thus the "active" study.
    const activeStudy = studies[0];

    // run the hanging protocol matching on the displaySets with the predefined
    // hanging protocol in the mode configuration
    hangingProtocolService.run({ studies, activeStudy, displaySets }, hangingProtocolId, {
      stageIndex,
    });
  }

  const unsubscriptions = [];
  const issuedWarningSeries = [];
  const { unsubscribe: instanceAddedUnsubscribe } = DicomMetadataStore.subscribe(
    DicomMetadataStore.EVENTS.INSTANCES_ADDED,
    function ({ StudyInstanceUID, SeriesInstanceUID, madeInClient = false }) {
      const seriesMetadata = DicomMetadataStore.getSeries(StudyInstanceUID, SeriesInstanceUID);

      // checks if the series filter was used, if it exists
      const seriesInstanceUIDs = filters?.seriesInstanceUID;
      if (
        seriesInstanceUIDs?.length &&
        !isSeriesFilterUsed(seriesMetadata.instances, filters) &&
        !issuedWarningSeries.includes(seriesInstanceUIDs[0])
      ) {
        // stores the series instance filter so it shows only once the warning
        issuedWarningSeries.push(seriesInstanceUIDs[0]);
        uiNotificationService.show({
          title: 'Series filter',
          message: `Each of the series in filter: ${seriesInstanceUIDs} are not part of the current study. The entire study is being displayed`,
          type: 'error',
          duration: 7000,
        });
      }

      displaySetService.makeDisplaySets(seriesMetadata.instances, { madeInClient });
    }
  );

  unsubscriptions.push(instanceAddedUnsubscribe);

  log.time(Enums.TimingEnum.STUDY_TO_DISPLAY_SETS);
  log.time(Enums.TimingEnum.STUDY_TO_FIRST_IMAGE);

  // First validate that all studies exist by searching for them
  // This prevents further PACS API calls if a study has failed
  const studyValidationPromises = studyInstanceUIDs.map(async StudyInstanceUID => {
    if (isImportedStudyUID(StudyInstanceUID)) {
      const study = DicomMetadataStore.getStudy(StudyInstanceUID);
      return { StudyInstanceUID, isValid: Boolean(study?.series?.length) };
    }

    try {
      const studies = await dataSource.query.studies.search({
        studyInstanceUid: StudyInstanceUID,
      });
      return { StudyInstanceUID, isValid: studies && studies.length > 0 };
    } catch (error) {
      // If study search fails (e.g., "Report not found"), mark as invalid
      console.warn(`Study validation failed for ${StudyInstanceUID}:`, error);
      return { StudyInstanceUID, isValid: false, error };
    }
  });

  const studyValidations = await Promise.allSettled(studyValidationPromises);
  const failedStudies: Array<{ StudyInstanceUID: string; error: unknown }> = [];
  const validStudies = studyValidations
    .map((result, index) => {
      if (result.status === 'fulfilled') {
        if (result.value.isValid) {
          return studyInstanceUIDs[index];
        } else if (result.value.error) {
          failedStudies.push({
            StudyInstanceUID: studyInstanceUIDs[index],
            error: result.value.error,
          });
        }
      }
      return null;
    })
    .filter(Boolean) as string[];

  // Show modal for failed studies (but not for expired patient links — those use /notfoundstudy)
  if (failedStudies.length > 0) {
    const firstError = failedStudies[0].error;
    const statusCode =
      (firstError as { statusCode?: number; status?: number })?.statusCode ??
      (firstError as { statusCode?: number; status?: number })?.status;
    const errorMessage =
      firstError instanceof Error
        ? firstError.message
        : 'Report not found for this patient and study';

    if (statusCode === 410) {
      try {
        sessionStorage.setItem(
          'ris_patient_link_study_uids',
          failedStudies.map(s => s.StudyInstanceUID).join(',')
        );
      } catch {
        // ignore storage errors
      }
      window.location.replace('/notfoundstudy?reason=expired');
      return unsubscriptions;
    }

    const { uiModalService } = servicesManager.services;
    if (uiModalService) {
      // Close any active Shepherd.js tour/onboarding before showing error modal
      try {
        // Method 1: Try to access Shepherd via global or window
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const shepherd = (window as any).shepherd || (window as any).Shepherd;
        if (shepherd?.activeTour) {
          shepherd.activeTour.complete();
        }

        // Method 2: Find and click the "Skip all" button via DOM
        const shepherdElement = document.querySelector('.shepherd-element');
        if (shepherdElement) {
          // Look for "Skip all" button (secondary button)
          const skipButton = Array.from(shepherdElement.querySelectorAll('.shepherd-button')).find(
            btn => btn.textContent?.toLowerCase().includes('skip')
          ) as HTMLElement;
          if (skipButton) {
            skipButton.click();
          }
        }

        // Method 3: Hide Shepherd overlay if it exists
        const shepherdOverlay = document.querySelector('.shepherd-modal-overlay-container');
        if (shepherdOverlay) {
          (shepherdOverlay as HTMLElement).style.display = 'none';
        }
      } catch (error) {
        console.warn('Could not close Shepherd tour:', error);
      }

      // Inject style to ensure modal overlay and content are above Shepherd
      const styleId = 'error-modal-z-index-override';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          /* Ensure error modal appears above Shepherd tour */
          [data-radix-dialog-overlay] {
            z-index: 10000 !important;
          }
          [data-radix-dialog-content] {
            z-index: 10001 !important;
          }
        `;
        document.head.appendChild(style);
      }

      // Use setTimeout to ensure modal renders after current execution context
      setTimeout(() => {
        uiModalService.show({
          title: 'Study Not Found',
          content: () => {
            return React.createElement(
              'div',
              { className: 'text-foreground' },
              React.createElement('p', { className: 'mb-4' }, errorMessage),
              React.createElement(
                'div',
                { className: 'flex justify-center' },
                React.createElement(
                  'button',
                  {
                    className: 'px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark',
                      onClick: async () => {
                        uiModalService.hide();
                        try {
                          clearPatientMode();
                          await authService.logout({ scope: 'patient' });
                          window.location.href = '/patient/login';
                        } catch (error) {
                          console.error('Error during logout:', error);
                          authService.clearPatientAuth();
                          window.location.href = '/patient/login';
                        }
                      },
                  },
                  'Close'
                )
              )
            );
          },
          shouldCloseOnEsc: true,
          shouldCloseOnOverlayClick: true,
          containerClassName: '!z-[10001]', // Higher z-index than Shepherd (typically 10000+), !important to override
        });
      }, 150); // Small delay to ensure Shepherd is closed first
    } else {
      console.error('uiModalService is not available');
    }
  }

  // If no valid studies found, return early to prevent further API calls
  if (validStudies.length === 0) {
    console.warn('No valid studies found. Skipping metadata retrieval.');
    return unsubscriptions;
  }

  // Invalidate cached study metadata so reopening picks up new series (e.g. saved SR).
  validStudies.forEach(StudyInstanceUID => {
    if (!isImportedStudyUID(StudyInstanceUID)) {
      dataSource.deleteStudyMetadataPromise?.(StudyInstanceUID);
    }
  });

  // Only retrieve metadata for valid server-backed studies
  const serverBackedStudies = validStudies.filter(
    StudyInstanceUID => !isImportedStudyUID(StudyInstanceUID)
  );

  const allRetrieves = serverBackedStudies.map(StudyInstanceUID =>
    dataSource.retrieve.series.metadata({
      StudyInstanceUID,
      filters,
      returnPromises: true,
      sortCriteria: customizationService.getCustomization('sortingCriteria'),
    })
  );

  // Surface failures without leaving unhandled rejections (dev overlay) or silent-only logs
  serverBackedStudies.forEach((StudyInstanceUID, index) => {
    allRetrieves[index].catch(error => {
      console.error(error);
      uiNotificationService?.show({
        title: 'Unable to load study metadata',
        message:
          error instanceof Error
            ? error.message
            : typeof error === 'object' && error !== null && 'message' in error
              ? String((error as { message: unknown }).message)
              : String(error),
        type: 'error',
        duration: 10000,
        id: `study-metadata-route-${StudyInstanceUID}`,
        allowDuplicates: false,
      });
    });
  });

  // is displaysets from URL and has initialSOPInstanceUID or initialSeriesInstanceUID
  // then we need to wait for all display sets to be retrieved before applying the hanging protocol
  const params = new URLSearchParams(window.location.search);

  const initialSeriesInstanceUID = getSplitParam('initialseriesinstanceuid', params);
  const initialSOPInstanceUID = getSplitParam('initialsopinstanceuid', params);

  let displaySetFromUrl = false;
  if (initialSeriesInstanceUID || initialSOPInstanceUID) {
    displaySetFromUrl = true;
  }

  await Promise.allSettled(allRetrieves).then(async promises => {
    log.timeEnd(Enums.TimingEnum.STUDY_TO_DISPLAY_SETS);
    log.time(Enums.TimingEnum.DISPLAY_SETS_TO_FIRST_IMAGE);
    log.time(Enums.TimingEnum.DISPLAY_SETS_TO_ALL_IMAGES);

    const allPromises = [];
    const remainingPromises = [];

    function startRemainingPromises(remainingPromises) {
      remainingPromises.forEach(p => p.forEach(p => p.start()));
    }

    promises.forEach(promise => {
      const retrieveSeriesMetadataPromise = promise.value;
      if (!Array.isArray(retrieveSeriesMetadataPromise)) {
        return;
      }

      if (displaySetFromUrl) {
        const requiredSeriesPromises = retrieveSeriesMetadataPromise.map(promise =>
          promise.start()
        );
        allPromises.push(Promise.allSettled(requiredSeriesPromises));
      } else {
        const { requiredSeries, remaining } = hangingProtocolService.filterSeriesRequiredForRun(
          hangingProtocolId,
          retrieveSeriesMetadataPromise
        );
        const requiredSeriesPromises = requiredSeries.map(promise => promise.start());
        allPromises.push(Promise.allSettled(requiredSeriesPromises));
        remainingPromises.push(remaining);
      }
    });

    await Promise.allSettled(allPromises);
    bootstrapDisplaySetsForStudies(displaySetService, validStudies);
    applyHangingProtocol();
    startRemainingPromises(remainingPromises);
    applyHangingProtocol();
  });

  return unsubscriptions;
}
